import { cn } from '@ctct/summit-core';
import { getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { useTranslations } from 'next-intl';
import {
  type ElementRef,
  forwardRef,
  type ForwardRefExoticComponent,
  useMemo,
  useState,
} from 'react';
import { Controller, type FieldValues, useFormContext } from 'react-hook-form';
import ReactPhoneNumberInput, {
  type Country,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumber,
  type Props,
  type Value,
} from 'react-phone-number-input';

import { type InputOpts } from '../../../types';
import { CountrySelect } from './CountrySelect';
import { PhoneNumberInput } from './PhoneNumberInput';

type PhoneInputProps = Omit<React.ComponentProps<'input'>, 'onChange' | 'value' | 'ref'>
  & Omit<Props<typeof ReactPhoneNumberInput>, 'onChange'> & {
    onChange: (value: Value) => void;
    isAutoFill?: boolean;
    shouldDisableAutofill?: boolean;
    shouldEnableCountryCallingCode?: boolean;
  };

const PhoneInput: ForwardRefExoticComponent<PhoneInputProps> = forwardRef<
  ElementRef<typeof ReactPhoneNumberInput>,
  PhoneInputProps
>(
  (
    {
      className,
      onChange: handleOnChange,
      value,
      onBlur: handleOnBlur,
      countryCallingCodeEditable: isCountryCallingCodeEditable = true,
      defaultCountry,
      isAutoFill = false,
      shouldDisableAutofill = false,
      disabled: isDisabled,
      ...props
    },
    ref
  ) => {
    const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(defaultCountry);
    const phoneNumberConfig = useMemo(() => {
      // If isAutoFill is true, we don't want to show the country code
      // There is bug that prevents the input from working if the phone number is auto filled
      // instead of typing it in.
      if (!selectedCountry || isAutoFill) return {};
      return {
        countryCode: `+${getCountryCallingCode(selectedCountry)}`,
        nationalNumber: getExampleNumber(selectedCountry, examples)
          ?.formatNational()
          .replace(/\d/g, '0'),
      };
    }, [selectedCountry, isAutoFill]);

    return (
      <ReactPhoneNumberInput
        ref={ref}
        className={cn('rds-phone-number-input r:flex r:w-full', className)}
        limitMaxLength
        countrySelectComponent={CountrySelect}
        countrySelectProps={{
          onBlur: handleOnBlur,
          disabled: !isCountryCallingCodeEditable,
        }}
        inputComponent={PhoneNumberInput}
        numberInputProps={{ phoneNumberConfig, shouldDisableAutofill, disabled: isDisabled }}
        onChange={handleOnChange}
        onCountryChange={(country: Country) => setSelectedCountry(country)}
        value={value}
        disabled={isDisabled}
        countryCallingCodeEditable={isCountryCallingCodeEditable}
        onBlur={handleOnBlur}
        defaultCountry={defaultCountry}
        {...props}
      />
    );
  }
);

const InternationalPhoneInput = ({
  formKey,
  registerOpts,
  defaultCountry,
  onChange,
  isAutoFill = false,
  shouldDisableAutofill = false,
  shouldEnableCountryCallingCode = true,
  ...props
}: InputOpts
  & Omit<PhoneInputProps, 'onChange'> & {
    onChange?: (value: Value) => void;
    isAutoFill?: boolean;
    shouldDisableAutofill?: boolean;
    shouldEnableCountryCallingCode?: boolean;
  }) => {
  const t = useTranslations('UiForm');
  const { control, getValues } = useFormContext();

  const getPhoneNumber = (value: Value) => parsePhoneNumber(value);

  // If there is a default country, we will use that as the initial value
  // If the value is already set, we want to use the country from the phone number
  // If there is no value or the value can't be parsed, we will get it from the locale or fallback to US
  const defaultCountryValue = useMemo(() => {
    if (defaultCountry) return defaultCountry;

    const value = getValues(formKey);
    if (value) {
      const parsed = getPhoneNumber(value);
      if (parsed?.country) return parsed.country;
    }

    const userLocale = Intl.DateTimeFormat().resolvedOptions().locale;
    const inferredCountry = userLocale.split('-')[1];

    if (inferredCountry && getCountries().includes(inferredCountry as Country))
      return inferredCountry as Country;

    return 'US' as Country;
  }, [defaultCountry, formKey, getValues]);

  const isValid = (value?: string) => {
    if (value && !isValidPhoneNumber(value, defaultCountryValue))
      return t('shared.phoneValidation.invalidNumber');
  };

  const combinedValidate = (value: string, formValues: FieldValues) => {
    const existingValidate =
      registerOpts?.validate && typeof registerOpts.validate === 'function'
        ? registerOpts.validate
        : undefined;

    const existingValidationResult = existingValidate
      ? existingValidate(value, formValues)
      : undefined;

    if (existingValidationResult) return existingValidationResult;
    return isValid(value);
  };

  const handleValueChange = (value: Value, fieldOnChange: (...event: unknown[]) => void) => {
    fieldOnChange(value ?? null);
    onChange?.(value);
  };

  return (
    <Controller
      name={formKey}
      control={control}
      rules={{
        ...registerOpts,
        validate: combinedValidate,
      }}
      render={({ field: { onChange, ...field } }) => {
        return (
          <PhoneInput
            // Can't use international if defaultCountryValue is undefined
            international={defaultCountryValue === undefined}
            {...field}
            {...props}
            onChange={(value: Value) => handleValueChange(value, onChange)}
            defaultCountry={defaultCountryValue}
            isAutoFill={isAutoFill}
            shouldDisableAutofill={shouldDisableAutofill}
            countryCallingCodeEditable={shouldEnableCountryCallingCode}
          />
        );
      }}
    />
  );
};

export { InternationalPhoneInput };
